import {
  forwardRef,
  useEffect,
  useId,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import CustomButton from "./Button";
import { CloseButton } from "./CloseButton";
import { getNotchMaskStyle } from "./notchMask";

/**
 * Dialog — the single, canonical overlay primitive (audit Section 4,
 * UX-09..12).
 *
 * Before this existed, every modal / drawer / panel reinvented its own
 * chrome: three different (or zero) ways to close, inconsistent footer
 * button order, ad-hoc disabled-until-valid behaviour, and a coin-flip
 * between a centered card and a right drawer for comparable "edit
 * settings" tasks. `Dialog` centralises ALL of that so a restyle is a
 * one-file change and every overlay reads as one family.
 *
 * Two layouts, one component:
 *   - `variant="center"` (default): a centered card over a dimmed
 *     backdrop. The everyday modal/dialog (confirmations, small forms,
 *     report viewers).
 *   - `variant="drawer"`: a right-edge sliding panel in the
 *     Notifications / ChatV2 visual family (cream+haze gradient header +
 *     right-edge notch cutout). For richer "settings panel" tasks.
 *
 * Structure is always header / scrollable body / sticky footer:
 *   ┌─────────────────────────────┐
 *   │ title / subtitle   extra  ✕ │  ← header (fixed)
 *   ├─────────────────────────────┤
 *   │ children …                  │  ← body (scrolls, d-scroll-sm)
 *   ├─────────────────────────────┤
 *   │           [secondary][primary] │ ← footer (sticky)
 *   └─────────────────────────────┘
 *
 * Footer: pass `primaryAction` / `secondaryAction` for the STANDARD
 * layout (secondary left, primary right, disabled-until-valid via
 * `primaryAction.disabled`). For a bespoke footer pass `footer` raw — it
 * wins over the action props. Omit all three and the footer disappears.
 *
 * The close affordance (shared `CloseButton`) is mandatory by default —
 * pass `showClose={false}` only for flows that must not be dismissed via
 * the header (rare).
 */

const framerBackdrop = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0, transition: { delay: 0.1 } },
  transition: { duration: 0.3 },
};

const framerCenterPanel = {
  initial: { opacity: 0, scale: 0.98, y: 8 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.98, y: 8, transition: { duration: 0.15 } },
  transition: { duration: 0.2, ease: [0.32, 0.72, 0, 1] as const },
};

const framerDrawerBackdrop = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0, transition: { delay: 0.1 } },
  transition: { duration: 0.3 },
};

const framerDrawerPanel = {
  initial: { x: "100%" },
  animate: { x: 0 },
  exit: { x: "100%" },
  transition: { duration: 0.3 },
};

/** A standardized footer button. */
export interface DialogAction {
  label: ReactNode;
  onClick: () => void;
  /** Maps to `CustomButton` variant. Defaults: primary→default, secondary→outline. */
  variant?: "default" | "secondary" | "link" | "ghost" | "outline" | "brand";
  disabled?: boolean;
  loading?: boolean;
  /** `data-attr` for analytics / e2e selectors. */
  dataAttr?: string;
  /** Override the button className (e.g. width). */
  className?: string;
}

type DialogSize = "sm" | "md" | "lg" | "xl";

const SIZE_TO_MAXW: Record<DialogSize, string> = {
  sm: "md:max-w-md", // 28rem
  md: "md:max-w-lg", // 32rem
  lg: "md:max-w-2xl", // 42rem
  xl: "md:max-w-4xl", // 56rem
};

export interface DialogProps {
  /** Dismiss handler — wired to backdrop click, ESC, and the close button. */
  onClose: () => void;
  variant?: "center" | "drawer";
  /** Header title. Rendered in the canonical panel-title type. */
  title?: ReactNode;
  /** Secondary line under the title (e.g. "Price Override"). */
  subtitle?: ReactNode;
  /** Extra header content placed left of the close button (date, toggle…). */
  headerExtra?: ReactNode;
  /** Scrollable body content. */
  children?: ReactNode;
  /** Raw footer — wins over primary/secondary action props. */
  footer?: ReactNode;
  primaryAction?: DialogAction;
  secondaryAction?: DialogAction;
  /** Centered-variant width preset. Ignored by `drawer`. */
  size?: DialogSize;
  /**
   * Override the centered-variant width with explicit classes (e.g.
   * `"md:max-w-6xl 3xl:max-w-[70vw]"`). Wins over `size`. Ignored by `drawer`.
   */
  centerWidthClassName?: string;
  /** Drawer-variant width classes (default 800px on md+). */
  drawerWidthClassName?: string;
  /** Show the header close button. Default true (audit UX-09). */
  showClose?: boolean;
  closeAriaLabel?: string;
  /** `data-attr` on the close button. */
  closeDataAttr?: string;
  /** Drop the divider under the header. */
  hideHeaderBorder?: boolean;
  /** Extra classes for the panel surface. */
  className?: string;
  /** Extra classes for the scrollable body. */
  bodyClassName?: string;
  /** z-index utility for the overlay (default `z-[70]`). */
  zIndexClassName?: string;
  /** Backdrop click closes the dialog. Default true. */
  closeOnBackdrop?: boolean;
  /** ESC closes the dialog. Default true. */
  closeOnEsc?: boolean;
}

function FooterButton({
  action,
  fallbackVariant,
}: {
  action: DialogAction;
  fallbackVariant: DialogAction["variant"];
}) {
  return (
    <CustomButton
      variant={action.variant ?? fallbackVariant}
      disabled={action.disabled}
      loading={action.loading}
      onClick={action.onClick}
      className={action.className}
      {...(action.dataAttr ? { "data-attr": action.dataAttr } : {})}
    >
      {action.label}
    </CustomButton>
  );
}

export const Dialog = forwardRef<HTMLDivElement, DialogProps>(function Dialog(
  {
    onClose,
    variant = "center",
    title,
    subtitle,
    headerExtra,
    children,
    footer,
    primaryAction,
    secondaryAction,
    size = "md",
    centerWidthClassName,
    drawerWidthClassName = "md:w-[800px]",
    showClose = true,
    closeAriaLabel = "Close",
    closeDataAttr,
    hideHeaderBorder = false,
    className = "",
    bodyClassName = "",
    zIndexClassName = "z-[70]",
    closeOnBackdrop = true,
    closeOnEsc = true,
  },
  ref,
) {
  // Lock body scroll while open.
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // ESC to close.
  useEffect(() => {
    if (!closeOnEsc) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, closeOnEsc]);

  const handleBackdrop = () => {
    if (closeOnBackdrop) onClose();
  };

  // Stable id so the panel can be `aria-labelledby` the title (programmatic
  // accessible name for the dialog).
  const titleId = useId();
  const labelledBy = title != null ? titleId : undefined;

  const hasHeader = title != null || subtitle != null || headerExtra != null || showClose;

  // Title typography differs by surface scale: the drawer is a large panel
  // (Notifications / ChatV2 family, 28px/500), the centered card is a
  // smaller surface where 28px would shout (20px/600 headline).
  const titleStyle: CSSProperties =
    variant === "drawer"
      ? {
          fontFamily: "var(--d-font-sans)",
          fontWeight: 500,
          fontSize: 28,
          lineHeight: 1.15,
          letterSpacing: "-0.025em",
          color: "var(--d-ink)",
        }
      : {
          fontFamily: "var(--d-font-sans)",
          fontWeight: 600,
          fontSize: 20,
          lineHeight: 1.2,
          letterSpacing: "-0.01em",
          color: "var(--d-ink)",
        };

  // The drawer's gradient header already separates itself from the body, so
  // it carries no divider; the centered card uses the divider unless asked
  // to hide it.
  const showHeaderBorder = variant === "center" && !hideHeaderBorder;

  const header = hasHeader ? (
    <div
      className={`flex-shrink-0 flex justify-between items-center gap-3 px-6 py-4 ${
        showHeaderBorder ? "border-b border-[#E6E6E6]" : ""
      }`}
    >
      <div className="flex flex-col gap-0.5 min-w-0">
        {title != null && (
          <p id={titleId} className="m-0 truncate" style={titleStyle}>
            {title}
          </p>
        )}
        {subtitle != null && (
          <span className="text-content-tertiary text-sm font-normal leading-tight">
            {subtitle}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {headerExtra}
        {showClose && (
          <CloseButton
            onClick={onClose}
            aria-label={closeAriaLabel}
            {...(closeDataAttr ? { "data-attr": closeDataAttr } : {})}
          />
        )}
      </div>
    </div>
  ) : null;

  const footerContent =
    footer ??
    (primaryAction || secondaryAction ? (
      <div className="flex items-center justify-end gap-3">
        {secondaryAction && (
          <FooterButton action={secondaryAction} fallbackVariant="outline" />
        )}
        {primaryAction && (
          <FooterButton action={primaryAction} fallbackVariant="default" />
        )}
      </div>
    ) : null);

  const footerNode = footerContent ? (
    <div className="flex-shrink-0 border-t border-[#E6E6E6] bg-surface-primary px-6 py-4">
      {footerContent}
    </div>
  ) : null;

  const body = (
    <div
      className={`flex-1 min-h-0 overflow-y-auto px-6 py-4 d-scroll-sm ${bodyClassName}`}
    >
      {children}
    </div>
  );

  if (variant === "drawer") {
    const drawerSurfaceStyle: CSSProperties = {
      // Notification-panel family look: cream bloom top-left + cool-gray
      // haze top-right fading into the surface, plus the shared right-edge
      // notch cutout. Same gradient + getNotchMaskStyle as NotificationPanel
      // / ChatV2 so the surfaces feel like one family.
      background:
        "radial-gradient(120% 100% at 10% 0%, rgba(241,235,229,0.9), rgba(241,235,229,0) 65%), radial-gradient(80% 100% at 100% 0%, rgba(214,220,228,0.45), rgba(214,220,228,0) 70%), var(--d-surface)",
      ...getNotchMaskStyle({ topPx: 55 }),
    };

    return createPortal(
      // Re-establish the `refined-teal` token scope: the portal mounts on
      // <body>, OUTSIDE the dashboard's `data-design` wrapper, so without this
      // the `var(--d-*)` tokens used by the gradient, CloseButton/GlassButton
      // glass chrome and any `.d-*` content would not resolve. `display:
      // contents` keeps the wrapper invisible to layout.
      <div data-design="refined-teal" className="contents">
        <motion.div
          {...framerDrawerBackdrop}
          className={`fixed inset-0 ${zIndexClassName} bg-black/40 backdrop-blur-[4px]`}
          onClick={handleBackdrop}
        />
        <motion.div
          {...framerDrawerPanel}
          className={`fixed top-0 right-0 h-[100dvh] flex justify-end ${zIndexClassName}`}
        >
          <div
            ref={ref}
            role="dialog"
            aria-modal="true"
            aria-labelledby={labelledBy}
            className={`w-[calc(100%-24px)] ${drawerWidthClassName} m-3 self-stretch flex flex-col rounded-2xl shadow-2xl border border-border-primary overflow-hidden ${className}`}
            style={drawerSurfaceStyle}
          >
            {header}
            {body}
            {footerNode}
          </div>
        </motion.div>
      </div>,
      document.body,
    );
  }

  // center variant
  return createPortal(
    // See the drawer branch — re-establish the token scope inside the portal.
    <div
      data-design="refined-teal"
      className={`fixed inset-0 h-[100dvh] flex items-center justify-center ${zIndexClassName}`}
    >
      <motion.div
        {...framerBackdrop}
        className="fixed inset-0 bg-black/40"
        onClick={handleBackdrop}
      />
      <motion.div
        {...framerCenterPanel}
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className={`relative z-[1] w-[calc(100%-24px)] ${
          centerWidthClassName ?? SIZE_TO_MAXW[size]
        } max-h-[90vh] flex flex-col rounded-2xl shadow-2xl border border-border-primary overflow-hidden bg-surface-primary ${className}`}
      >
        {header}
        {body}
        {footerNode}
      </motion.div>
    </div>,
    document.body,
  );
});
